import { useReducer, useEffect, Reducer } from 'react';

type ErrorMap = { [key: string]: boolean };
type OptimisticEntity<T> = T; // returns sync before completed dispatch
export type UseForm<Entity> = [UseFormState<Entity>, UseFormDispatch<Entity>];
export interface UseFormState<Entity> {
  form: Entity;
  errorMap: ErrorMap;
}

/**
 * NOTE: 'value' is assumed to be a string here, but can be easily configured to accept many values or generic
 */
export interface UseFormDispatch<Entity> {
  onFormChange: (name: string, value: string) => OptimisticEntity<Entity>;
  setForm: (nextFormValue: Entity) => void;
  setFormErrorMap: (errors: ErrorMap) => void;
}

type Action<Entity> =
  | { type: 'FORM_CHANGED'; payload: { name: string; value: string } }
  | { type: 'REVERT_FORM' }
  | { type: 'SET_FORM'; payload: Entity }
  | { type: 'SET_FORM_ERROR_MAP'; payload: ErrorMap };

const getInitialState = <Entity>(): UseFormState<Entity> => ({
  form: {} as Entity,
  errorMap: {}
});

const formReducer = <Entity extends object>(
  state: UseFormState<Entity>,
  action: Action<Entity>
): UseFormState<Entity> => {
  switch (action.type) {
    case 'FORM_CHANGED':
      return {
        ...state,
        form: { ...state.form, [action.payload.name]: action.payload.value }
      };
    case 'SET_FORM':
      return {
        ...state,
        form: { ...action.payload }
      };
    case 'SET_FORM_ERROR_MAP':
      return {
        ...state,
        errorMap: action.payload
      };
    default:
      return getInitialState<Entity>();
  }
};

const useForm = <Entity extends object>(initialFormState = {} as Entity): UseForm<Entity> => {
  const [state, dispatch] = useReducer<Reducer<UseFormState<Entity>, Action<Entity>>>(
    formReducer,
    getInitialState<Entity>()
  );

  useEffect(() => {
    if (initialFormState) setForm(initialFormState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOptimisticUpdate = (name: string, value: string): Entity => ({
    ...state.form,
    [name]: value
  });

  const onFormChange = (name: string, value: string): OptimisticEntity<Entity> => {
    dispatch({ type: 'FORM_CHANGED', payload: { name, value } });

    return getOptimisticUpdate(name, value);
  };
  const setForm = (nextFormValue: Entity): void =>
    dispatch({ type: 'SET_FORM', payload: nextFormValue });

  const setFormErrorMap = (errors: ErrorMap): void =>
    dispatch({ type: 'SET_FORM_ERROR_MAP', payload: errors });

  return [
    state,
    {
      onFormChange,
      setForm,
      setFormErrorMap
    }
  ];
};

export { useForm };
